import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "../../../context/AuthContext";

// Row shape the Payroll page renders. Language picks the displayed name.
const transformPayroll = (payroll, language) => {
          // Normalize an ISO datetime or date string to "YYYY-MM-DD" for HTML date inputs.
          // Returns "" if the value is falsy or not parseable.
          const toDateString = (val) => {
            if (!val) return "";
            try {
              const d = new Date(val);
              if (isNaN(d.getTime())) return "";
              return d.toISOString().slice(0, 10);
            } catch {
              return "";
            }
          };

          const employee = payroll.employee || {};

          const allowances = payroll.allowances || [];
          const overtimeAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "overtime",
          );
          const transportationAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "transportation",
          );
          const mealAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "meal",
          );
          const mobileAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "mobile",
          );
          const bonusAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "bonus",
          );
          const kpiAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "kpi_adjustment",
          );
          const expensesAllowance = allowances.find(
            (a) => a.type?.toLowerCase() === "expenses",
          );

          const deductions = payroll.deductions || [];
          const penaltyDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "penalty",
          );
          const advanceDeduction = deductions.find(
            (d) =>
              d.type?.toLowerCase() === "advancement" ||
              d.type?.toLowerCase() === "advance",
          );
          const insuranceDeduction = deductions.find(
            (d) =>
              d.type?.toLowerCase() === "insurance" ||
              d.type?.toLowerCase() === "social_insurance",
          );
          const absenceDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "absence",
          );
          const sickLeaveDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "sick_leave_25",
          );
          const delayDeduction = deductions.find(
            (d) => d.type?.toLowerCase() === "delay",
          );

          const allowancesTotal = allowances.reduce(
            (sum, a) => sum + (Number(a.amount) || 0),
            0,
          );
          const deductionsTotal = deductions.reduce(
            (sum, d) => sum + (Number(d.amount) || 0),
            0,
          );

          return {
            id: payroll.id,
            employeeName: (() => {
              const name =
                language === "ar" ? employee.arabicName : employee.sr;

              return typeof name === "string" && name.trim()
                ? name.trim().split(/\s+/).slice(0, 3).join(" ")
                : "N/A";
            })(),

            employeeId: `EMP${String(payroll.infinityCode).padStart(3, "0")}`,
            infinityCode: payroll.infinityCode,
            clientCode: employee.clientCode || "",
            department:
              employee.department?.name ??
              employee.position ??
              employee.employeeType,
            workingCompany: employee.workingCompany,
            laborMode: employee.laborMode || "",
            englishName: employee.sr || employee.englishName || "",
            arabicName: employee.arabicName || "",
            nationalId: employee.nationalId || "",
            phoneNumber: employee.phoneNumber || "",
            bankName: employee.bankName || "",
            accountNumber: employee.accountNumber || "",
            insuranceNumber: employee.insuranceNumber || "",

            position: employee.position ?? employee.employeeType ?? "N/A",

            basicSalary: Number(payroll.basicSalary) || 0,

            bonus: Number(bonusAllowance?.amount || payroll.bonusValue) || 0,
            bonusAdjustments: Number(kpiAllowance?.amount) || 0,
            kpiScore: Number(payroll.achievement) || 0,

            overtimeHours:
              Number(overtimeAllowance?.hours || payroll.overtimeHours) || 0,
            overtimeTotal:
              Number(overtimeAllowance?.amount || payroll.overtimeValue) || 0,

            transportationAllowance:
              Number(transportationAllowance?.amount || payroll.transportationAllowances) || 0,
            mealAllowance: Number(mealAllowance?.amount) || 0,
            mobileAllowance: Number(mobileAllowance?.amount) || 0,
            expenses:
              Number(expensesAllowance?.amount || payroll.expenses) || 0,

            grossSalary: Number(payroll.grossSalary) || 0,
            netSalary: Number(payroll.netSalary) || 0,

            taxMonth: Number(payroll.monthlyTax) || 0,
            martyrsFund: Number(payroll.martyrsFund) || 0,

            socialInsurance:
              // An insurance line if there is one, otherwise 11% of the
              // insurance salary. No insurance salary on file = 0 — never a
              // fallback to the basic salary.
              Number(insuranceDeduction?.amount) ||
              (Number(employee.insuranceSalary) > 0 ? Number(employee.insuranceSalary) * 0.11 : 0),
            penalty:
              Number(penaltyDeduction?.amount || payroll.penalityDaysValue) ||
              0,
            advancePayment: Number(advanceDeduction?.amount) || 0,
            absenceDeduction:
              Number(absenceDeduction?.amount || payroll.absenceDaysValue) || 0,
            sickLeaveDeduction:
              Number(
                sickLeaveDeduction?.amount || payroll.sickLeave25DeductionValue,
              ) || 0,
            delayDeduction: Number(delayDeduction?.amount) || 0,
            totalDeductions: Number(payroll.totalDeductions) || deductionsTotal,
            totalAllowances: Number(payroll.totalAllowances) || allowancesTotal,

            hiringDate: toDateString(employee.hiringDate),
            insuranceSalary: Number(employee.insuranceSalary) || 0,
            // Company share = 18.75% of insurance salary (Egyptian law)
            insuranceCompanyShare: Math.round((Number(employee.insuranceSalary) || 0) * 0.1875),
            // Emergency fund = 1% of insurance salary
            emergencyFund: Math.round((Number(employee.insuranceSalary) || 0) * 0.01),
            markup: Number(payroll.markup) || 0,

            workingDays:
              Math.max(
                0,
                Number(payroll.workingDays || 0) - Number(payroll.absenceDays || 0),
              ) || 0,
            absenceDays: Number(payroll.absenceDays) || 0,
            penalityDays: payroll.penalityDays || 0,
            sickLeave25DeductionDays: payroll.sickLeave25DeductionDays || 0,

            status: payroll.paymentStatus,
            paidDate: payroll.paymentDate,
            paymentMethod: payroll.paymentMethod,

            year: payroll.year,
            month: payroll.month,
            payrollStartDate: payroll.payrollStartDate,
            payrollEndDate: payroll.payrollEndDate,

            personalInfo: {
              email: employee.email || "",
              phone: employee.phoneNumber || "",
              emergencyPhone: employee.emergencyPhoneNumber || "",
              nationalId: employee.nationalId || "",
              dateOfBirth: toDateString(employee.dob),
              gender: employee.gender || "",
              maritalStatus: employee.maritalStatus || "",
              nationality: employee.nationality || "",
              address: employee.address || "",
              city: employee.city || "",
              country: employee.country || "Egypt",
              religion: employee.religion || "",
              age: employee.age || null,
            },

            workInfo: {
              hiringDate: toDateString(employee.hiringDate),
              contractType: employee.laborMode || "Full-time",
              contractCompany: employee.contractCompany || "",
              workingHours: "40 hours/week",
              probationPeriod: employee.periodForBeingRegular || "3 months",
              contractExpiration: toDateString(employee.contractExpiration),
              directManager: employee.firstLineManagerName || "",
              workLocation: employee.location || "",
              employeeType: employee.position ?? employee.employeeType ?? "",
              beRegularDate: employee.beRegularDate || "",
              insuranceStatus: employee.insuranceStatus || "",
              insuranceNumber: employee.insuranceNumber || "",
              insuranceSalary: employee.insuranceSalary || 0,
            },

            bankInfo: {
              bankName: employee.bankName || "",
              accountNumber: employee.accountNumber || "",
              iban: employee.iban || "",
              branchCode: employee.branchCode || "",
            },

            notes: payroll.notes || "",
            isIgnored: payroll.isIgnored || false,
            isImported: payroll.isImported === true,
            // An imported payroll's figures with no payroll column (insurance
            // shares, tax breakdown, cost, markup, ...) — the export shows them.
            importedValues:
              payroll.importedValues && typeof payroll.importedValues === "object"
                ? payroll.importedValues
                : null,
            // Figures the import calculated because the file did not give them
            // (with provenance) — see the backend's payrollImportFigures.js.
            calculatedValues:
              payroll.calculatedValues && typeof payroll.calculatedValues === "object"
                ? payroll.calculatedValues
                : null,
            importedAt: payroll.importedAt || null,
            importedBy: payroll.importedBy || null,
            calculatedBy: payroll.calculatedBy,
            approvedBy: payroll.approvedBy,
            approvedDate: payroll.approvedDate,

            allowances: allowances.map((a) => ({
              ...a,
              amount: Number(a.amount) || 0,
            })),
            deductions: deductions.map((d) => ({
              ...d,
              amount: Number(d.amount) || 0,
            })),
          };
};

const PAGE_SIZE = 10;

const usePayrollData = ({
  selectedMonth,
  statusFilter,
  selectedDepartment,
  selectedCompany,
  selectedLaborMode = "All",
  searchTerm = "",
  visibilityRules,
  fetchVisibilityRules,
  profile,
  clientName,
  language,
}) => {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  // State for payroll data — ONE server page at a time. Filtering, sorting,
  // pagination and the header totals all happen in SQL (/payrollX/visible,
  // /visible/summary, /visible/ids); the browser never holds the whole month.
  const [payrollData, setPayrollData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [togglingIgnoredIds, setTogglingIgnoredIds] = useState(new Set());
  const [unpayingIds, setUnpayingIds] = useState(new Set());

  // Search goes to the server — debounce it so typing doesn't fire a request per key.
  const [debouncedSearch, setDebouncedSearch] = useState(String(searchTerm || "").trim());
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(String(searchTerm || "").trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const year = Number(selectedMonth?.year) || new Date().getFullYear();
  const month = Number(selectedMonth?.month) || new Date().getMonth() + 1;

  const buildParams = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    params.set("year", String(year));
    params.set("month", String(month));
    if (statusFilter && statusFilter !== "All") params.set("paymentStatus", statusFilter.toLowerCase());
    // Legacy: a numeric "department" is a single-employee filter by code.
    if (selectedDepartment && selectedDepartment !== "All") {
      if (!isNaN(selectedDepartment)) params.set("infinityCode", String(selectedDepartment));
      else params.set("department", String(selectedDepartment));
    }
    if (selectedCompany && selectedCompany !== "All") params.set("company", String(selectedCompany));
    if (selectedLaborMode && selectedLaborMode !== "All") params.set("laborMode", String(selectedLaborMode));
    if (debouncedSearch) params.set("search", debouncedSearch);
    Object.entries(extra).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    });
    return params;
  }, [year, month, statusFilter, selectedDepartment, selectedCompany, selectedLaborMode, debouncedSearch]);

  // Any filter change starts again from page 1.
  useEffect(() => {
    setPage(1);
  }, [year, month, statusFilter, selectedDepartment, selectedCompany, selectedLaborMode, debouncedSearch]);

  // Fetch the current page and the summary together.
  useEffect(() => {
    let cancelled = false;
    const fetchPayrollData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [listRes, summaryRes] = await Promise.all([
          apiFetch(`${API_BASE_URL}/payrollX/visible?${buildParams({ page, limit: PAGE_SIZE })}`, { method: "GET" }),
          apiFetch(`${API_BASE_URL}/payrollX/visible/summary?${buildParams()}`, { method: "GET" }),
        ]);
        if (!listRes.ok) {
          const errorText = await listRes.text();
          throw new Error(`HTTP error! status: ${listRes.status} - ${errorText}`);
        }
        const listJson = await listRes.json();
        const summaryJson = summaryRes.ok ? await summaryRes.json() : null;
        if (cancelled) return;

        const rows = Array.isArray(listJson?.data) ? listJson.data : [];
        setPayrollData(rows.map((p) => transformPayroll(p, language)));
        setPagination(listJson?.pagination ?? { page, limit: PAGE_SIZE, total: rows.length, totalPages: 1 });
        setSummary(summaryJson?.data ?? null);
      } catch (err) {
        if (cancelled) return;
        console.error("❌ Error fetching payroll data:", err);
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPayrollData();
    return () => {
      cancelled = true;
    };
  }, [page, buildParams, refreshTrigger, language, API_BASE_URL]);

  // Every row matching the current filters (all pages) — for exports, and
  // with { monthOnly: true } every visible row of the month regardless of the
  // table's filters — for the Process Payroll / Bulk Unpay modals, which must
  // offer the caller's whole visibility scope, not the page on screen.
  const fetchAllFiltered = useCallback(async (options = {}) => {
    const limit = 500;
    const params = options.monthOnly
      ? (extra = {}) => {
          const p = new URLSearchParams({ year: String(year), month: String(month) });
          Object.entries(extra).forEach(([k, v]) => p.set(k, String(v)));
          return p;
        }
      : buildParams;
    const first = await apiFetch(`${API_BASE_URL}/payrollX/visible?${params({ page: 1, limit })}`, { method: "GET" });
    if (!first.ok) throw new Error(`HTTP error! status: ${first.status}`);
    const firstJson = await first.json();
    let rows = Array.isArray(firstJson?.data) ? [...firstJson.data] : [];
    const totalPages = Number(firstJson?.pagination?.totalPages) || 1;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          apiFetch(`${API_BASE_URL}/payrollX/visible?${params({ page: i + 2, limit })}`, { method: "GET" })
            .then((r) => (r.ok ? r.json() : { data: [] })),
        ),
      );
      for (const j of rest) rows = rows.concat(Array.isArray(j?.data) ? j.data : []);
    }
    return rows.map((p) => transformPayroll(p, language));
  }, [API_BASE_URL, buildParams, language, year, month]);

  // Ids (+ status) of every row matching the filters — for "select all".
  const fetchFilteredIds = useCallback(async (extra = {}) => {
    const res = await apiFetch(`${API_BASE_URL}/payrollX/visible/ids?${buildParams(extra)}`, { method: "GET" });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    return Array.isArray(json?.data) ? json.data : [];
  }, [API_BASE_URL, buildParams]);

  // ==================== AUTO-CREATE PAYROLL ON LOAD ====================
  // Once per month per mount: ask the server to create the month's missing
  // rows for EVERY employee in the caller's payroll visibility scope. The
  // scope is resolved server-side (same rule as /payrollX/visible), so this
  // no longer depends on the visibility-rules request having finished, and
  // the server only touches active employees that still lack a row.
  const ensuredMonthsRef = useRef(new Set());
  useEffect(() => {
    if (loading || error) return;
    const key = `${year}-${month}`;
    if (ensuredMonthsRef.current.has(key)) return;
    ensuredMonthsRef.current.add(key);

    (async () => {
      try {
        console.log(`🔄 Ensuring payroll exists for the visibility scope (payroll month ${year}/${month})...`);
        const response = await apiFetch(`${API_BASE_URL}/payrollX/ensure-exists`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ year, month }),
        });
        const result = await response.json();
        const created = result?.data?.created ?? 0;
        if (created > 0) {
          console.log(`✅ Auto-created ${created} payroll record(s) — refreshing...`);
          setRefreshTrigger((prev) => prev + 1);
        }
      } catch (err) {
        console.warn("⚠️ ensurePayrollExists call failed:", err.message);
      }
    })();
  }, [loading, error, year, month, API_BASE_URL]);

  // Function to refresh data
  const refreshData = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleToggleIsIgnored = async (payrollId) => {
    setTogglingIgnoredIds((prev) => new Set([...prev, payrollId]));
    try {
      const resp = await apiFetch(
        `${API_BASE_URL}/payrollX/${payrollId}/toggle-ignored`,
        {
          method: "PATCH",
        },
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to toggle");
      // Refresh full data so recalculated payroll numbers are reflected
      refreshData();
    } catch (err) {
      console.error("Toggle isIgnored failed:", err.message);
    } finally {
      setTogglingIgnoredIds((prev) => {
        const next = new Set(prev);
        next.delete(payrollId);
        return next;
      });
    }
  };

  // Bulk version of handleToggleIsIgnored above — used by the table's
  // selection tool. Doing this as N calls to the single-row endpoint (one
  // HTTP round trip, one DB write, and one full payroll recalculation per
  // selected employee, each followed by its own refreshData()/full refetch)
  // was heavy for anything more than a handful of rows. This instead hits
  // one bulk endpoint that updates every row and reprocesses them grouped by
  // period server-side, then refreshes the table data exactly once here.
  const handleBulkToggleIsIgnored = async (payrollIds, isIgnored) => {
    if (!Array.isArray(payrollIds) || payrollIds.length === 0) return;
    setTogglingIgnoredIds((prev) => {
      const next = new Set(prev);
      payrollIds.forEach((id) => next.add(id));
      return next;
    });
    try {
      const resp = await apiFetch(`${API_BASE_URL}/payrollX/bulk/toggle-ignored`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payrollIds, isIgnored }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to bulk toggle");
      refreshData();
      return data;
    } finally {
      setTogglingIgnoredIds((prev) => {
        const next = new Set(prev);
        payrollIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const handleUnpayPayroll = async (payrollId, callbacks = {}) => {
    const { onSuccess, onError } = callbacks;
    setUnpayingIds((prev) => new Set([...prev, payrollId]));
    try {
      const resp = await apiFetch(
        `${API_BASE_URL}/payrollX/${payrollId}/unpay`,
        {
          method: "PATCH",
        },
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to unpay");
      // Refresh full data so updated payroll status is reflected
      refreshData();
      if (onSuccess) onSuccess(data);
    } catch (err) {
      console.error("Unpay payroll failed:", err.message);
      if (onError) onError(err);
    } finally {
      setUnpayingIds((prev) => {
        const next = new Set(prev);
        next.delete(payrollId);
        return next;
      });
    }
  };

  // Bulk version of handleUnpayPayroll — same motivation as
  // handleBulkToggleIsIgnored above: N calls to the single-row endpoint (N
  // round trips, each followed by its own refreshData()/full refetch) was
  // heavy for a multi-row selection even though unpaying itself is a cheap
  // field reset. One request updates every selected row, then this refreshes
  // the table data exactly once.
  const handleBulkUnpayPayroll = async (payrollIds) => {
    if (!Array.isArray(payrollIds) || payrollIds.length === 0) {
      return { updated: 0, skipped: 0 };
    }
    setUnpayingIds((prev) => {
      const next = new Set(prev);
      payrollIds.forEach((id) => next.add(id));
      return next;
    });
    try {
      const resp = await apiFetch(`${API_BASE_URL}/payrollX/bulk/unpay`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payrollIds }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to bulk unpay");
      refreshData();
      return data.data;
    } finally {
      setUnpayingIds((prev) => {
        const next = new Set(prev);
        payrollIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const refreshPayrollData = () => {
    console.log("🔄 Refreshing payroll data...");
    setRefreshTrigger((prev) => prev + 1);
  };

  return {
    payrollData,
    setPayrollData,
    loading,
    error,
    pagination,
    summary,
    page,
    setPage,
    fetchAllFiltered,
    fetchFilteredIds,
    refreshTrigger,
    setRefreshTrigger,
    togglingIgnoredIds,
    unpayingIds,
    refreshData,
    handleToggleIsIgnored,
    handleBulkToggleIsIgnored,
    handleUnpayPayroll,
    handleBulkUnpayPayroll,
    refreshPayrollData,
  };
};

export default usePayrollData;